import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Stack,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  TextField,
} from '@mui/material'
import { BootstrapDialog } from 'components/BootstrapDialog'
import { DialogCloseButton } from 'components/DialogCloseButton'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'
import { useCreateFaqMutation } from 'services/info-center/faq'
import { createFaqSchema } from './validations'
import { IInfoCenterFaq } from 'types/info-center/faq'

export type FaqFieldValues = {
  question: string
  answer: string
}

type Props = {
  onClose: () => void
  faq?: IInfoCenterFaq
}

export const DialogCreateFaq = ({ onClose, faq }: Props) => {
  const { t } = useTranslation()
  const schema = createFaqSchema(t)

  const [createFaq, { isSuccess, isError }] = useCreateFaqMutation()

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm<FaqFieldValues>({
    defaultValues: {
      question: faq?.question ?? '',
      answer: faq?.answer ?? '',
    },
    resolver: zodResolver(schema),
  })

  useEffect(() => {
    if (isSuccess) {
      onClose()
      toast.success(t('operation-success'))
    }
    if (isError) {
      toast.error(t('failedToCreate'))
    }
  }, [isSuccess, isError, t, onClose])

  const onSubmit = ({ question, answer }: FaqFieldValues) => {
    if (faq) {
      createFaq({
        id: faq?.id,
        question,
        answer,
      })
    } else {
      createFaq({
        id: uuidv4(),
        question,
        answer,
      })
    }
  }

  return (
    <BootstrapDialog open maxWidth="sm" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, fontWeight: 'bold' }}>{t('faq')}</DialogTitle>

      <DialogCloseButton handleClose={onClose} />

      <DialogContent dividers>
        <Stack spacing={2}>
          <Controller
            name="question"
            control={control}
            render={({ field }) => (
              <Box>
                <TextField
                  {...field}
                  fullWidth
                  required
                  label={t('question')}
                  size="small"
                  variant="outlined"
                />
                {errors.question && (
                  <FormHelperText error>{errors.question?.message}</FormHelperText>
                )}
              </Box>
            )}
          />
          <Controller
            name="answer"
            control={control}
            render={({ field }) => (
              <Box>
                <TextField
                  {...field}
                  fullWidth
                  required
                  label={t('answer')}
                  size="small"
                  multiline
                  minRows={3}
                  variant="outlined"
                />
                {errors.answer && (
                  <FormHelperText error>{errors.answer?.message}</FormHelperText>
                )}
              </Box>
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Stack width="100%" direction="row" justifyContent="space-between">
          <Button onClick={onClose} variant="outlined">
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
            autoFocus
            onClick={handleSubmit(onSubmit)}
          >
            {t('create')}
          </Button>
        </Stack>
      </DialogActions>
    </BootstrapDialog>
  )
}
